import { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useSeaCheckApiProfileAccessQuery } from "entities/SeaCheckApiProfile";
import { subscriptionStore } from "entities/Subscription";
import { useKeypress } from "shared/hooks/useKeypress.hook";
import { useOnClickOutside } from "shared/hooks/useOnClickOutside.hook";
import { useWindowSize } from "shared/hooks/useWindowSize.hook";
import { useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { Button, Link } from "shared/ui/Button";
import { ArrowTopRightIcon, FreePaymentsIcon, PaidPaymentsIcon } from "shared/ui/Icons/Icons";
import { AppDOM } from "shared/utils/app.utils";
import { formatCardDate } from "shared/utils/dateTime.utils";
import { PRICING_PAGE_URL } from "shared/utils/url.utils";
import styles from "./SeaPayments.module.scss";

export const SeaPayments: RFC = () => {
  const { t } = useTranslation();
  const paymentButtonRef = useRef<HTMLButtonElement>(null);
  const { resetSeaCheckApiProfileAccess } = useSeaCheckApiProfileAccessQuery({
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
  });
  const subscription = useAppSelector(subscriptionStore.selectors.subscription);
  const { width } = useWindowSize({ debounceTime: 70 });

  const isIconsVisible = useMemo(() => {
    if (!width) return false;
    return width < 768;
  }, [width]);

  const [isOpenPaymentContent, setIsOpenPaymentContent] = useState(false);
  const paymentContentWrapperRef = useRef<HTMLDivElement>(null);

  const subscriptionUsedValue = useMemo(() => {
    if (!subscription?.used || !subscription.total) return 0;
    return subscription.used > subscription.total ? subscription.total : subscription.used;
  }, [subscription?.used, subscription?.total]);

  const isRemaining = useMemo(() => {
    if (!subscription?.remaining || subscription.remaining < 1) return false;
    return true;
  }, [subscription?.remaining]);

  useEffect(() => {
    return () => {
      resetSeaCheckApiProfileAccess();
    };
  }, []);

  const closePaymentContent = () => {
    setIsOpenPaymentContent(false);
  };

  useOnClickOutside({
    ref: paymentContentWrapperRef,
    handler: closePaymentContent,
    excludeRefs: [paymentButtonRef],
    shadowRoot: AppDOM.shadowRootNode,
    triggerDependencies: [isOpenPaymentContent],
  });

  useKeypress("Escape", closePaymentContent);

  if (!subscription) {
    return null;
  }

  return (
    <div className={styles.paymentsWrapper}>
      {!subscription.isFree ? (
        <Button
          ref={paymentButtonRef}
          onClick={() => {
            setIsOpenPaymentContent((prev) => !prev);
          }}
          variant="primary"
          className={cn(styles.planButton, styles.subscription, { [styles.isIconsVisible]: isIconsVisible })}
          data-test-id="payments-subscription-button"
        >
          {isIconsVisible ? <PaidPaymentsIcon /> : t("Subscription")}
        </Button>
      ) : (
        <Button
          ref={paymentButtonRef}
          onClick={() => {
            setIsOpenPaymentContent((prev) => !prev);
          }}
          variant="primary"
          className={cn(styles.planButton, styles.upgrade, { [styles.isIconsVisible]: isIconsVisible })}
          data-test-id="payments-upgrade-button"
        >
          {isIconsVisible ? <FreePaymentsIcon /> : t("Upgrade")}
        </Button>
      )}
      {isOpenPaymentContent && (
        <div ref={paymentContentWrapperRef} className={styles.paymentContentWrapper}>
          <div className={styles.row}>
            <p className={styles.rowTitle}>{t("Plan")}</p>
            {subscription.isFree ? (
              <span className={cn(styles.tag, styles.tag__free)} data-test-id="payments-data-plan-free">
                {t("Free")}
              </span>
            ) : (
              <span className={cn(styles.tag, styles.tag__paid)} data-test-id="payments-data-plan-paid">
                {t("Paid")}
              </span>
            )}
          </div>
          {subscription.endDate && (
            <div className={styles.row}>
              <p className={styles.rowTitle}>{t("Valid till")}</p>
              <span className={styles.date} data-test-id="payments-data-valid-till">
                {formatCardDate(subscription.endDate)}
              </span>
            </div>
          )}
          <div className={styles.row}>
            <p className={styles.rowTitle}>{t("Credits used")}</p>
            <div className={styles.remaining}>
              <span className={styles.remaining__current} data-test-id="payments-data-credits-used">
                {subscriptionUsedValue}
              </span>
              <span className={styles.remaining__Total} data-test-id="payments-data-credits-total">
                /{subscription.total}
              </span>
            </div>
          </div>
          {isRemaining ? (
            <>
              {subscription.isFree ? (
                <Link
                  href={PRICING_PAGE_URL}
                  target="_blank"
                  variant="primary"
                  className={cn(styles.contactButton)}
                  data-test-id="payments-upgrade-plan-button"
                >
                  {t("Upgrade plan for more shipments")}
                  <ArrowTopRightIcon />
                </Link>
              ) : (
                <Link
                  href={PRICING_PAGE_URL}
                  target="_blank"
                  className={cn(styles.contactLink)}
                  onClick={closePaymentContent}
                  data-test-id="payments-extend-plan-button"
                >
                  {t("Purchase additional credits")}
                  <ArrowTopRightIcon />
                </Link>
              )}
            </>
          ) : (
            <div className={styles.error}>
              <p className={styles.error__description} data-test-id="payments-data-error-description">
                {t("You have reached the limit, purchase additional credits")}.
              </p>
              <Link
                href={PRICING_PAGE_URL}
                target="_blank"
                variant="primary"
                className={cn(styles.error__button)}
                data-test-id="payments-extend-plan-button"
              >
                {t("Extend")}
                <ArrowTopRightIcon />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
